import { useState, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, X, FileText, Download, Copy, CheckCircle, Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/authContext";

export type PdfToolId =
  | "info"
  | "merge"
  | "extract-pages"
  | "rotate"
  | "text"
  | "metadata"
  | "bookmarks-list"
  | "bookmarks-export"
  | "bookmarks-add"
  | "bookmarks-import"
  | "bookmarks-remove"
  | "optimize"
  | "from-images"
  | "password-remove"
  | "password-add"
  | "ocr"
  | "repair"
  | "vision-extract"
  | "smart-split";

interface Props {
  toolId: PdfToolId;
  toolName: string;
  onClose: () => void;
}

const TOOL_CONFIG: Record<PdfToolId, { endpoint: string; multi?: boolean; imageOnly?: boolean; resultType: "pdf" | "csv" | "json-info" | "json-text" | "json-bookmarks" | "json-vision" | "json-split" }> = {
  "info":              { endpoint: "/api/tools/pdf/info",              resultType: "json-info" },
  "merge":             { endpoint: "/api/tools/pdf/merge",             multi: true, resultType: "pdf" },
  "extract-pages":     { endpoint: "/api/tools/pdf/extract-pages",     resultType: "pdf" },
  "rotate":            { endpoint: "/api/tools/pdf/rotate",            resultType: "pdf" },
  "text":              { endpoint: "/api/tools/pdf/text",              resultType: "json-text" },
  "metadata":          { endpoint: "/api/tools/pdf/metadata",          resultType: "pdf" },
  "bookmarks-list":    { endpoint: "/api/tools/pdf/bookmarks/list",    resultType: "json-bookmarks" },
  "bookmarks-export":  { endpoint: "/api/tools/pdf/bookmarks/export",  resultType: "csv" },
  "bookmarks-add":     { endpoint: "/api/tools/pdf/bookmarks/add",     resultType: "pdf" },
  "bookmarks-import":  { endpoint: "/api/tools/pdf/bookmarks/import",  resultType: "pdf" },
  "bookmarks-remove":  { endpoint: "/api/tools/pdf/bookmarks/remove",  resultType: "pdf" },
  "optimize":          { endpoint: "/api/tools/pdf/optimize",          resultType: "pdf" },
  "from-images":       { endpoint: "/api/tools/pdf/from-images",       multi: true, imageOnly: true, resultType: "pdf" },
  "password-remove":   { endpoint: "/api/tools/pdf/password/remove",   resultType: "pdf" },
  "password-add":      { endpoint: "/api/tools/pdf/password/add",      resultType: "pdf" },
  "ocr":               { endpoint: "/api/tools/pdf/ocr",               imageOnly: true, resultType: "json-text" },
  "repair":            { endpoint: "/api/tools/pdf/repair",            resultType: "pdf" },
  "vision-extract":    { endpoint: "/api/tools/pdf/vision-extract",    imageOnly: true, resultType: "json-vision" },
  "smart-split":       { endpoint: "/api/tools/pdf/smart-split",       resultType: "json-split" },
};

function DropZone({
  files, onFiles, multi, imageOnly, label,
}: { files: File[]; onFiles: (f: File[]) => void; multi?: boolean; imageOnly?: boolean; label?: string }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const accept = imageOnly ? "image/jpeg,image/png,image/tiff,image/bmp" : "application/pdf";

  function addFiles(newFiles: FileList | null) {
    if (!newFiles) return;
    const arr = Array.from(newFiles).filter(f =>
      imageOnly ? f.type.startsWith("image/") : f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")
    );
    if (multi) onFiles([...files, ...arr]);
    else if (arr.length) onFiles([arr[0]]);
  }

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setDragging(false);
    addFiles(e.dataTransfer.files);
  }, [files]);

  return (
    <div>
      {label && <Label className="mb-1.5 block text-xs">{label}</Label>}
      <div
        className={cn("border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors",
          dragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-muted/30")}
        onClick={() => inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <Upload className="w-5 h-5 mx-auto mb-1.5 text-muted-foreground" />
        <p className="text-xs text-muted-foreground">
          {imageOnly ? "Drop JPEG/PNG images" : "Drop PDF file(s)"}
          {multi ? " or click to browse (multiple)" : " or click to browse"}
        </p>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={accept}
          multiple={multi}
          onChange={e => addFiles(e.target.files)}
        />
      </div>
      {files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.map((f, i) => (
            <li key={i} className="flex items-center gap-2 text-xs bg-muted/40 rounded px-2 py-1">
              <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <span className="flex-1 truncate">{f.name}</span>
              <span className="text-muted-foreground shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
              <button onClick={(e) => { e.stopPropagation(); onFiles(files.filter((_, j) => j !== i)); }} className="shrink-0">
                <X className="w-3 h-3 text-muted-foreground hover:text-destructive" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function PdfToolModal({ toolId, toolName, onClose }: Props) {
  const { session } = useAuth();
  const config = TOOL_CONFIG[toolId];

  const [files, setFiles] = useState<File[]>([]);
  const [csvFile, setCsvFile] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [copied, setCopied] = useState(false);

  // Tool-specific state
  const [pages, setPages] = useState("all");
  const [degrees, setDegrees] = useState("90");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaAuthor, setMetaAuthor] = useState("");
  const [metaSubject, setMetaSubject] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");
  const [metaCreator, setMetaCreator] = useState("");
  const [outputFilename, setOutputFilename] = useState("");
  const [bookmarkRows, setBookmarkRows] = useState<{ title: string; page: string }[]>([{ title: "", page: "" }]);
  const [removeAll, setRemoveAll] = useState(true);
  const [removeTitles, setRemoveTitles] = useState("");
  const [password, setPassword] = useState("");
  const [userPass, setUserPass] = useState("");
  const [ownerPass, setOwnerPass] = useState("");
  const [visionPrompt, setVisionPrompt] = useState("");
  const [splitSegments, setSplitSegments] = useState<{ name: string; pageRange: string; data: string }[] | null>(null);

  async function handleSubmit() {
    setError(null);
    if (!files.length) { setError("Please upload a file first."); return; }
    if (toolId === "merge" && files.length < 2) { setError("Upload at least 2 PDFs to merge."); return; }

    const fd = new FormData();
    const token = session?.access_token;

    if (toolId === "merge") {
      files.forEach(f => fd.append("files", f));
    } else if (toolId === "from-images") {
      files.forEach(f => fd.append("images", f));
    } else if (toolId === "bookmarks-import") {
      fd.append("pdf", files[0]);
      if (!csvFile.length) { setError("Upload a CSV file."); return; }
      fd.append("csv", csvFile[0]);
    } else {
      fd.append("file", files[0]);
    }

    // Extra params
    if (toolId === "extract-pages") fd.append("pages", pages);
    if (toolId === "rotate") { fd.append("pages", pages); fd.append("degrees", degrees); }
    if (toolId === "metadata") {
      if (metaTitle) fd.append("title", metaTitle);
      if (metaAuthor) fd.append("author", metaAuthor);
      if (metaSubject) fd.append("subject", metaSubject);
      if (metaKeywords) fd.append("keywords", metaKeywords);
      if (metaCreator) fd.append("creator", metaCreator);
    }
    if (toolId === "bookmarks-add") {
      const valid = bookmarkRows.filter(r => r.title.trim() && r.page.trim());
      if (!valid.length) { setError("Add at least one bookmark row."); return; }
      fd.append("bookmarks", JSON.stringify(valid.map(r => ({ title: r.title, page: parseInt(r.page) }))));
    }
    if (toolId === "bookmarks-remove") {
      fd.append("removeAll", String(removeAll));
      if (!removeAll) fd.append("titles", JSON.stringify(removeTitles.split("\n").map(s => s.trim()).filter(Boolean)));
    }
    if (toolId === "password-remove") fd.append("password", password);
    if (toolId === "password-add") {
      if (!userPass) { setError("User password is required."); return; }
      fd.append("userPass", userPass);
      fd.append("ownerPass", ownerPass || userPass);
    }
    if (toolId === "vision-extract" && visionPrompt.trim()) fd.append("prompt", visionPrompt.trim());
    if (outputFilename) fd.append("filename", outputFilename.replace(/\.pdf$/i, ""));

    setLoading(true);
    try {
      const resp = await fetch(config.endpoint, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ message: resp.statusText }));
        throw new Error(err.message || "Request failed");
      }

      if (config.resultType === "pdf" || config.resultType === "csv") {
        const blob = await resp.blob();
        const contentDisposition = resp.headers.get("Content-Disposition") || "";
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        const filename = outputFilename
          ? outputFilename + (config.resultType === "csv" ? ".csv" : ".pdf")
          : (filenameMatch?.[1] ?? (config.resultType === "csv" ? "bookmarks.csv" : "output.pdf"));
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
        setResult({ downloaded: true, filename });
      } else if (config.resultType === "json-split") {
        const data = await resp.json();
        setSplitSegments(data.segments ?? []);
        setResult({ totalPages: data.totalPages, segmentCount: (data.segments ?? []).length });
      } else {
        const data = await resp.json();
        setResult(data);
      }
    } catch (e: any) {
      setError(e.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  function copyText(text: string) {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">{toolName}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          {/* File upload */}
          {toolId !== "bookmarks-import" ? (
            <DropZone
              files={files}
              onFiles={setFiles}
              multi={config.multi}
              imageOnly={config.imageOnly}
            />
          ) : (
            <>
              <DropZone files={files} onFiles={setFiles} label="PDF file" />
              <DropZone files={csvFile} onFiles={setCsvFile} label="CSV file (title,page columns)" />
            </>
          )}

          {/* Tool-specific inputs */}
          {(toolId === "extract-pages") && (
            <div>
              <Label className="text-xs mb-1.5 block">Pages to extract</Label>
              <Input
                value={pages}
                onChange={e => setPages(e.target.value)}
                placeholder="e.g. 1,3,5-7 or all"
                className="text-xs h-8"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Use commas and ranges, e.g. 1,3,5-10</p>
            </div>
          )}

          {toolId === "rotate" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1.5 block">Pages</Label>
                <Input value={pages} onChange={e => setPages(e.target.value)} placeholder="all or 1,3,5-7" className="text-xs h-8" />
              </div>
              <div>
                <Label className="text-xs mb-1.5 block">Rotation</Label>
                <Select value={degrees} onValueChange={setDegrees}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="90">90° clockwise</SelectItem>
                    <SelectItem value="180">180°</SelectItem>
                    <SelectItem value="270">270° (90° counter)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {toolId === "metadata" && (
            <div className="space-y-2">
              {[
                { label: "Title", val: metaTitle, set: setMetaTitle },
                { label: "Author", val: metaAuthor, set: setMetaAuthor },
                { label: "Subject", val: metaSubject, set: setMetaSubject },
                { label: "Keywords", val: metaKeywords, set: setMetaKeywords },
                { label: "Creator", val: metaCreator, set: setMetaCreator },
              ].map(({ label, val, set }) => (
                <div key={label} className="grid grid-cols-[80px_1fr] items-center gap-2">
                  <Label className="text-xs text-right">{label}</Label>
                  <Input value={val} onChange={e => set(e.target.value)} className="text-xs h-7" placeholder={`Leave blank to keep existing`} />
                </div>
              ))}
            </div>
          )}

          {toolId === "bookmarks-add" && (
            <div>
              <Label className="text-xs mb-2 block">Bookmarks to add</Label>
              <div className="space-y-1.5">
                {bookmarkRows.map((row, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      value={row.title}
                      onChange={e => setBookmarkRows(rows => rows.map((r, j) => j === i ? { ...r, title: e.target.value } : r))}
                      placeholder="Bookmark title"
                      className="text-xs h-7 flex-1"
                    />
                    <Input
                      value={row.page}
                      onChange={e => setBookmarkRows(rows => rows.map((r, j) => j === i ? { ...r, page: e.target.value } : r))}
                      placeholder="Page #"
                      className="text-xs h-7 w-20"
                      type="number"
                      min={1}
                    />
                    <button onClick={() => setBookmarkRows(rows => rows.filter((_, j) => j !== i))} disabled={bookmarkRows.length === 1}>
                      <Trash2 className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
                    </button>
                  </div>
                ))}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 h-7 text-xs text-muted-foreground"
                onClick={() => setBookmarkRows(r => [...r, { title: "", page: "" }])}
              >
                <Plus className="w-3 h-3 mr-1" /> Add row
              </Button>
            </div>
          )}

          {toolId === "bookmarks-remove" && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="radio" checked={removeAll} onChange={() => setRemoveAll(true)} />
                  Remove all bookmarks
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="radio" checked={!removeAll} onChange={() => setRemoveAll(false)} />
                  Remove by title
                </label>
              </div>
              {!removeAll && (
                <div>
                  <Label className="text-xs mb-1.5 block">Titles to remove (one per line)</Label>
                  <Textarea
                    value={removeTitles}
                    onChange={e => setRemoveTitles(e.target.value)}
                    placeholder="Section 1&#10;Chapter 2&#10;Appendix A"
                    className="text-xs min-h-[80px] resize-none"
                  />
                </div>
              )}
            </div>
          )}

          {toolId === "password-remove" && (
            <div>
              <Label className="text-xs mb-1.5 block">Current password (leave blank if unknown — qpdf will try without one)</Label>
              <Input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Current PDF password"
                className="text-xs h-8"
              />
            </div>
          )}

          {toolId === "password-add" && (
            <div className="space-y-2">
              <div>
                <Label className="text-xs mb-1.5 block">User password <span className="text-destructive">*</span></Label>
                <Input
                  type="password"
                  value={userPass}
                  onChange={e => setUserPass(e.target.value)}
                  placeholder="Password required to open the PDF"
                  className="text-xs h-8"
                />
              </div>
              <div>
                <Label className="text-xs mb-1.5 block">Owner password <span className="text-muted-foreground">(optional — controls editing/printing)</span></Label>
                <Input
                  type="password"
                  value={ownerPass}
                  onChange={e => setOwnerPass(e.target.value)}
                  placeholder="Defaults to user password if blank"
                  className="text-xs h-8"
                />
              </div>
            </div>
          )}

          {toolId === "vision-extract" && (
            <div>
              <Label className="text-xs mb-1.5 block">Custom extraction prompt (optional)</Label>
              <Textarea
                value={visionPrompt}
                onChange={e => setVisionPrompt(e.target.value)}
                placeholder="e.g. Extract vendor name, invoice number, date, line items, and total amount"
                className="text-xs min-h-[72px] resize-none"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Leave blank to extract all data automatically</p>
            </div>
          )}

          {toolId === "smart-split" && (
            <p className="text-[10px] text-muted-foreground bg-violet-50 border border-violet-100 rounded px-3 py-2">
              AI will analyze the text in your PDF to detect document boundaries (e.g., separate invoices in a combined scan) and split them into individual PDFs.
            </p>
          )}

          {toolId === "repair" && (
            <p className="text-[10px] text-muted-foreground bg-amber-50 border border-amber-100 rounded px-3 py-2">
              Attempts to repair structural errors in a corrupted PDF using qpdf. Requires qpdf installed on the server.
            </p>
          )}

          {/* Output filename for PDF outputs */}
          {(config.resultType === "pdf" || config.resultType === "csv") && (
            <div>
              <Label className="text-xs mb-1.5 block">Output filename (optional)</Label>
              <Input
                value={outputFilename}
                onChange={e => setOutputFilename(e.target.value)}
                placeholder={config.resultType === "csv" ? "bookmarks" : "output"}
                className="text-xs h-8"
              />
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="rounded-md bg-destructive/10 border border-destructive/20 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="rounded-md border bg-muted/20 p-3 space-y-2">
              {result.downloaded && (
                <div className="flex items-center gap-2 text-xs text-green-700">
                  <CheckCircle className="w-3.5 h-3.5" />
                  <span>Downloaded: <strong>{result.filename}</strong></span>
                </div>
              )}

              {/* PDF Info */}
              {toolId === "info" && result.pageCount !== undefined && (
                <div className="text-xs space-y-1">
                  <InfoRow label="Pages" value={result.pageCount} />
                  <InfoRow label="File size" value={`${(result.fileSizeBytes / 1024).toFixed(1)} KB`} />
                  {result.title && <InfoRow label="Title" value={result.title} />}
                  {result.author && <InfoRow label="Author" value={result.author} />}
                  {result.subject && <InfoRow label="Subject" value={result.subject} />}
                  {result.keywords && <InfoRow label="Keywords" value={result.keywords} />}
                  {result.creator && <InfoRow label="Creator" value={result.creator} />}
                  {result.producer && <InfoRow label="Producer" value={result.producer} />}
                  {result.creationDate && <InfoRow label="Created" value={new Date(result.creationDate).toLocaleDateString()} />}
                  {result.modificationDate && <InfoRow label="Modified" value={new Date(result.modificationDate).toLocaleDateString()} />}
                  {result.pages?.length > 0 && (
                    <div className="mt-2">
                      <p className="font-medium mb-1">Page sizes</p>
                      <div className="max-h-32 overflow-y-auto space-y-0.5">
                        {result.pages.map((p: any) => (
                          <div key={p.index} className="flex gap-3 text-muted-foreground">
                            <span className="w-12">Pg {p.index}</span>
                            <span>{p.widthPt} × {p.heightPt} pt</span>
                            {p.rotation !== 0 && <span className="text-amber-600">{p.rotation}° rotated</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* OCR result */}
              {toolId === "ocr" && result.text !== undefined && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium">Confidence: {result.confidence}%</span>
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={() => copyText(result.text)}>
                      {copied ? <CheckCircle className="w-3 h-3 mr-1 text-green-600" /> : <Copy className="w-3 h-3 mr-1" />}
                      {copied ? "Copied" : "Copy"}
                    </Button>
                  </div>
                  <Textarea value={result.text} readOnly className="text-[10px] font-mono min-h-[120px] resize-none" />
                </div>
              )}

              {/* Extracted text */}
              {toolId === "text" && result.text !== undefined && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium">{result.pages} page(s) — {result.text.length.toLocaleString()} chars</span>
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={() => copyText(result.text)}>
                      {copied ? <CheckCircle className="w-3 h-3 mr-1 text-green-600" /> : <Copy className="w-3 h-3 mr-1" />}
                      {copied ? "Copied" : "Copy"}
                    </Button>
                  </div>
                  <Textarea value={result.text} readOnly className="text-[10px] font-mono min-h-[120px] resize-none" />
                </div>
              )}

              {/* Vision extract result */}
              {toolId === "vision-extract" && result && (
                <div className="space-y-3">
                  {result.structured && (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-medium">Extracted Data (JSON)</span>
                        <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={() => copyText(JSON.stringify(result.structured, null, 2))}>
                          {copied ? <CheckCircle className="w-3 h-3 mr-1 text-green-600" /> : <Copy className="w-3 h-3 mr-1" />}
                          {copied ? "Copied" : "Copy JSON"}
                        </Button>
                      </div>
                      <Textarea value={JSON.stringify(result.structured, null, 2)} readOnly className="text-[10px] font-mono min-h-[120px] resize-none" />
                    </div>
                  )}
                  {result.csv && (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-medium">CSV Output</span>
                        <button
                          className="text-[10px] text-primary underline"
                          onClick={() => {
                            const blob = new Blob([result.csv], { type: "text/csv" });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url; a.download = "extracted.csv"; a.click();
                            URL.revokeObjectURL(url);
                          }}
                        >
                          <Download className="w-3 h-3 inline mr-1" />Download CSV
                        </button>
                      </div>
                      <Textarea value={result.csv} readOnly className="text-[10px] font-mono min-h-[80px] resize-none" />
                    </div>
                  )}
                  {!result.structured && result.raw && (
                    <div>
                      <p className="text-xs font-medium mb-1.5">Raw Response</p>
                      <Textarea value={result.raw} readOnly className="text-[10px] font-mono min-h-[120px] resize-none" />
                    </div>
                  )}
                </div>
              )}

              {/* Smart split result */}
              {toolId === "smart-split" && splitSegments && (
                <div>
                  <p className="text-xs font-medium mb-2">
                    {splitSegments.length} document{splitSegments.length !== 1 ? "s" : ""} found in {result?.totalPages}-page PDF
                  </p>
                  <div className="space-y-1.5 max-h-64 overflow-y-auto">
                    {splitSegments.map((seg, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 rounded border bg-muted/20 px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-xs font-medium truncate">{seg.name}</p>
                          <p className="text-[10px] text-muted-foreground">{seg.pageRange}</p>
                        </div>
                        <button
                          className="shrink-0 flex items-center gap-1 text-[10px] text-primary font-medium hover:underline"
                          onClick={() => {
                            const bytes = Uint8Array.from(atob(seg.data), c => c.charCodeAt(0));
                            const blob = new Blob([bytes], { type: "application/pdf" });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `${seg.name.replace(/[^a-z0-9_\-\s]/gi, "_")}.pdf`;
                            a.click();
                            URL.revokeObjectURL(url);
                          }}
                        >
                          <Download className="w-3 h-3" /> Download
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Bookmarks list */}
              {toolId === "bookmarks-list" && (
                <div>
                  <p className="text-xs font-medium mb-1.5">
                    {result.bookmarks?.length ?? 0} bookmark(s) in {result.pageCount}-page PDF
                  </p>
                  {result.bookmarks?.length === 0 && (
                    <p className="text-xs text-muted-foreground">No bookmarks found in this PDF.</p>
                  )}
                  <div className="max-h-48 overflow-y-auto space-y-0.5">
                    {result.bookmarks?.map((b: any, i: number) => (
                      <div key={i} className="flex items-center gap-2 text-xs" style={{ paddingLeft: `${b.level * 12}px` }}>
                        <span className="text-muted-foreground shrink-0">›</span>
                        <span className="flex-1 truncate">{b.title}</span>
                        {b.page > 0 && <span className="text-muted-foreground shrink-0">p.{b.page}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={onClose} className="h-8 text-xs">Cancel</Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={loading || !files.length}
              className="h-8 text-xs"
            >
              {loading ? (
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing…
                </span>
              ) : config.resultType === "pdf" || config.resultType === "csv" ? (
                <span className="flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> Run & Download</span>
              ) : "Run"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InfoRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex gap-2">
      <span className="text-muted-foreground w-24 shrink-0">{label}</span>
      <span className="font-medium truncate">{value}</span>
    </div>
  );
}
